/**
 * V2 rebuild against Figma frame a_start_join_team (393×884).
 * Functional contract preserved: QR scan, manual code fallback, error mapping,
 * later-team-joining branch, sessionStorage write.
 */
import { Box, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { ApiError } from '../../api/client';
import { isAlreadyInTeam, isRunNotStarted, isTeamFull } from './joinErrorReasons';
import { joinAsGuest } from '../../api/guest';
import ClavisTextField from '../../components/ClavisTextField';
import OnboardingShellV2 from '../../components/OnboardingShellV2';
import ProgressIndicatorV2 from '../../components/ProgressIndicatorV2';
import heroJoin from '../../assets/onboarding/hero-team-join.svg';
import QrScanner from '../../components/qr/QrScanner';
import { useAuth } from '../../auth/useAuth';
import { useWizardState } from '../../wizard/useWizardState';
import { getStepInfo } from '../../wizard/wizardSteps';
import { pillButtonFocusRing } from '../../styles/onboardingButtons';

const extractTeamCode = (text: string): string => {
  try {
    const url = new URL(text);
    const teamParam = url.searchParams.get('team');
    if (teamParam) return teamParam.toUpperCase();
  } catch {
    // not a URL — treat as code
  }
  return text.trim().toUpperCase();
};

const JoinTeamStep: React.FC = () => {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const { code: rallyeCode } = useParams<{ code: string }>();
  const wizard = useWizardState();
  const { checkAuth } = useAuth();
  const tf = wizard.teamFlow;

  const [scannerOpen, setScannerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Synchronous in-flight guard: `submitting` (React state) only updates on
  // the next render, so two submit triggers landing in the same tick (Enter
  // keydown + button click, double-tap, QR decode + click) could both pass a
  // `!submitting` check and double-fire the join request (issue #257).
  const inFlightRef = useRef(false);

  const step = getStepInfo('team-join', 'with-code');

  const submitCode = async (rawCode: string) => {
    if (!rallyeCode) return;
    if (inFlightRef.current) return;
    const code = rawCode.trim().toUpperCase();
    if (!code) return;
    inFlightRef.current = true;
    setSubmitting(true);
    wizard.patchTeamFlow({ joinError: null });
    try {
      const resp = await joinAsGuest(rallyeCode, {
        mode: 'join',
        team_code: code,
        display_name: wizard.displayName || undefined,
      });
      wizard.patchTeamFlow({
        teamId: resp.team_id,
        teamShareCode: resp.team_share_code,
        teamNameFromServer: resp.team_name,
        runId: resp.run_id,
        isCreator: false,
        enteredCode: code,
      });
      await checkAuth();
      if (resp.team_name !== null) {
        try {
          sessionStorage.setItem(
            `clavis_team_for_run_${resp.run_id}`,
            resp.team_id,
          );
        } catch {
          // sessionStorage disabled
        }
        navigate(`/runs/${resp.run_id}/participant/stations`, { replace: true });
      } else {
        navigate(`/join/${rallyeCode}/team/waiting`);
      }
    } catch (err) {
      let mapped:
        | 'not_found'
        | 'full'
        | 'not_started'
        | 'rate_limited'
        | 'already_in_team'
        | 'unknown' = 'unknown';
      if (err instanceof ApiError) {
        if (err.status === 404) mapped = 'not_found';
        else if (err.status === 422) {
          // Backend details are single-language literals (guest.py sends
          // "Dieses Team ist voll." but "Run has not started yet."), so match
          // both German and English tokens for every category — mirrors the
          // InviteTeamStep mapping and survives backend-side rephrasing.
          // Being in another team is permanent from the participant's side and
          // has a trap next to it: a guest who leaves cannot re-enter the run
          // at all (SOW 8.2.6, #345), and the coordinator's only tool — removing
          // the membership — bans them the same way. On 29.07 someone hit this
          // and retried 21 times in four minutes, because the generic fallback
          // told them to try again (#494).
          if (isAlreadyInTeam(err.detail)) {
            mapped = 'already_in_team';
          } else if (isTeamFull(err.detail)) {
            mapped = 'full';
          } else if (isRunNotStarted(err.detail)) {
            mapped = 'not_started';
          }
        } else if (err.status === 429) mapped = 'rate_limited';
      }
      console.warn('[JoinTeamStep] join failed', err);
      wizard.patchTeamFlow({ joinError: mapped });
    } finally {
      inFlightRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <OnboardingShellV2
      progress={
        <ProgressIndicatorV2
          current={step.current}
          total={step.total}
        />
      }
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          paddingBlockStart: '52px',
          gap: '24px',
        }}
      >
        <Box
          component="img"
          src={heroJoin}
          alt=""
          sx={{
            width: '107px',
            height: '98px',
            alignSelf: 'center',
            display: 'block',
            objectFit: 'contain',
          }}
        />
        <Typography
          component="h1"
          sx={{
            fontFamily: "'Roboto', sans-serif",
            fontSize: '24px',
            fontWeight: 600,
            lineHeight: '32px',
            letterSpacing: 0,
            color: '#322F35',
            margin: 0,
          }}
        >
          {t('join.title')}
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Noto Sans', sans-serif",
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '24px',
            letterSpacing: '0.5px',
            color: '#322F35',
            // Half-leading compensation (figma-implement skill §6 sub-fix):
            // body/large CSS line-box has 3 px half-leading above the glyph,
            // Figma's text-bbox has 0 → pull the element up so the glyph
            // lands at Figma's y-coordinate.
            marginBlockStart: '-3px',
          }}
        >
          {t('join.body')}
        </Typography>
        {scannerOpen ? (
          <QrScanner
            size={260}
            onDecode={(text) => {
              setScannerOpen(false);
              const code = extractTeamCode(text);
              wizard.patchTeamFlow({ enteredCode: code });
              void submitCode(code);
            }}
            onError={() => {
              setScannerOpen(false);
              wizard.patchTeamFlow({ joinError: 'unknown' });
              const el = document.getElementById('team-code-input');
              el?.focus();
            }}
          />
        ) : (
          <Box
            component="button"
            type="button"
            onClick={() => {
              wizard.patchTeamFlow({ joinError: null });
              setScannerOpen(true);
            }}
            sx={{
              all: 'unset',
              boxSizing: 'border-box',
              cursor: 'pointer',
              alignSelf: 'flex-start',
              // Visible-gap calibration (figma-implement skill §6):
              // Body 1's -3 marginBlockStart (half-leading comp) propagates
              // 3 px upward to all subsequent siblings. Scan still needs
              // a net -6 cumulative shift to land on Figma's y=296, so its
              // own marginBlockStart is -3 (= -6 cumulative − -3 from Body 1).
              marginBlockStart: '-3px',
              minHeight: '40px',
              paddingInline: '16px',
              borderRadius: '100px',
              backgroundColor: '#4D69B9',
              color: '#FFFFFF',
              fontFamily: "'Roboto', sans-serif",
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '20px',
              letterSpacing: '0.1px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'center',
              '&:hover': { backgroundColor: '#3D54A0' },
              ...pillButtonFocusRing,
            }}
          >
            {t('join.scanCta')}
          </Box>
        )}
        <Typography
          sx={{
            fontFamily: "'Noto Sans', sans-serif",
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '24px',
            letterSpacing: '0.5px',
            color: '#322F35',
            // Half-leading compensation (mirror of Body 1, but inverted):
            // Body 2 sits below Scan whose -3 marginBlockStart already
            // pulled this element 3 px up. body/large half-leading would
            // pull another 3 px → net 6 too high. Push back down by 3 to
            // restore alignment with Figma.
            marginBlockStart: '3px',
          }}
        >
          {t('join.fallbackHint')}
        </Typography>
        <ClavisTextField
          id="team-code-input"
          label={t('join.codeLabel')}
          value={tf.enteredCode}
          onChange={(e) =>
            wizard.patchTeamFlow({
              enteredCode: e.target.value.toUpperCase(),
              joinError: null,
            })
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !submitting) void submitCode(tf.enteredCode);
          }}
          slotProps={{ htmlInput: { maxLength: 8 } }}
          helperText={
            tf.joinError !== null ? (
              <>
                {t(`join.errors.${tf.joinError}`)}
                {tf.joinError === 'not_found' && (
                  <Box
                    component="span"
                    sx={{ display: 'block', marginBlockStart: '4px' }}
                  >
                    {t('join.errors.scanHint')}
                  </Box>
                )}
              </>
            ) : (
              ' '
            )
          }
          error={tf.joinError !== null}
          disabled={submitting}
          fullWidth
          variant="outlined"
          sx={{
            // Half-leading + cascade compensation: Body 2 +3 propagates 3
            // upward into the text field's natural position; the field needs
            // a net -9 cumulative shift to land at Figma y=429. After
            // upstream comps the residual is -6 here.
            marginBlockStart: '-6px',
            // Figma error state (pick_team_name_error pattern): stroke + label +
            // supporting-text all #8C1D18. MUI's default error.main is #d32f2f.
            '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline':
              { borderColor: '#8C1D18' },
            '& .MuiOutlinedInput-root.Mui-error:hover .MuiOutlinedInput-notchedOutline':
              { borderColor: '#8C1D18' },
            '& .MuiInputLabel-root.Mui-error': { color: '#8C1D18' },
            '& .MuiFormHelperText-root.Mui-error': { color: '#8C1D18' },
          }}
        />
        {/* Mobile-UX add-on: Figma frame shows only Back here, but the
            soft-keyboard "Done" key isn't reliable for form submit on mobile.
            We render an explicit Join button next to Back as soon as the
            user has typed something. Hidden when the input is empty so the
            default state still matches Figma. */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '16px',
            // Visible-gap calibration (figma-implement skill §6):
            // Cumulative drift from all upstream compensations (Body 1 -3,
            // Scan -3, Body 2 +3, TF -6) = -9. To land Back-pill at Figma's
            // y=513, total cumulative shift must be -25, so own marginBlockStart
            // is -16. Includes the helperText-inside-MUI overflow (+20) and
            // Frame 34 wrapper offset (-4).
            marginBlockStart: '-16px',
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={() => navigate(-1)}
            sx={{
              all: 'unset',
              boxSizing: 'border-box',
              cursor: 'pointer',
              minHeight: '40px',
              paddingInline: '16px',
              borderRadius: '100px',
              border: '1px solid #CAC4D0',
              color: '#49454F',
              fontFamily: "'Roboto', sans-serif",
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '20px',
              letterSpacing: '0.1px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': { backgroundColor: 'rgba(73, 69, 79, 0.04)' },
              ...pillButtonFocusRing,
            }}
          >
            {t('join.back')}
          </Box>
          {tf.enteredCode.trim().length > 0 && (
            <Box
              component="button"
              type="button"
              onClick={() => void submitCode(tf.enteredCode)}
              disabled={submitting}
              sx={{
                all: 'unset',
                boxSizing: 'border-box',
                cursor: submitting ? 'not-allowed' : 'pointer',
                minHeight: '40px',
                paddingInline: '16px',
                borderRadius: '100px',
                backgroundColor: submitting
                  ? 'rgba(29, 27, 32, 0.12)'
                  : '#4D69B9',
                color: submitting ? '#605D64' : '#FFFFFF',
                fontFamily: "'Roboto', sans-serif",
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '20px',
                letterSpacing: '0.1px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  backgroundColor: submitting
                    ? 'rgba(29, 27, 32, 0.12)'
                    : '#3D54A0',
                },
                ...pillButtonFocusRing,
              }}
            >
              {t('join.submitTeam')}
            </Box>
          )}
        </Box>
      </Box>
    </OnboardingShellV2>
  );
};

export default JoinTeamStep;
